import React, { useState, useRef } from 'react';
import { IoMdAddCircleOutline } from 'react-icons/io';
import { MdDeleteOutline } from 'react-icons/md';
import { FileUploadToCloud } from '@app/services/fileUploadService';
import toast from 'react-hot-toast';
import { ImSpinner } from 'react-icons/im';
import Resizer from 'react-image-file-resizer';

const CookingSteps = ({
  value,
  setFieldValue,
  generateStepImage,
  generatingImages
}) => {
  const [uploadingImages, setUploadingImages] = useState({});
  const fileInputRefs = useRef({});

  const handleImageSelect = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match('image.*')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Set uploading state
    setUploadingImages((prev) => ({ ...prev, [idx]: true }));

    try {
      // Upload to Cloudinary
      const resizedFile = await new Promise((resolve, reject) => {
        Resizer.imageFileResizer(
          file,
          600,
          600,
          'JPEG',
          60,
          0,
          (uri) => resolve(uri),
          'file'
        );
      });
      const response = await FileUploadToCloud(resizedFile);

      if (response && response.secure_url) {
        // Update the instruction step with the image URL
        let steps = [...value.instruction_steps];
        steps[idx] = {
          ...steps[idx],
          image_url: response.secure_url
        };
        setFieldValue('instruction_steps', steps);
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      // Clear uploading state
      setUploadingImages((prev) => ({ ...prev, [idx]: false }));
      // Reset file input
      if (fileInputRefs.current[idx]) {
        fileInputRefs.current[idx].value = '';
      }
    }
  };

  const handleRemoveImage = (idx) => {
    let infos = value.instruction_steps;
    let info = infos[idx];
    const data = {
      ...info,
      image_url: undefined
    };
    infos[idx] = data;
    setFieldValue('instruction_steps', infos);
  };

  const handleImageClick = (idx) => {
    if (fileInputRefs.current[idx]) {
      fileInputRefs.current[idx].click();
    }
  };

  const addInstruction = () => {
    setFieldValue('instruction_steps', [
      ...value.instruction_steps,
      {
        step: '',
        time_estimate: undefined,
        image_url: undefined,
        video_url: undefined
      }
    ]);
  };

  const removeInstruction = (idx) => {
    const updatedData = value.instruction_steps.filter((_, i) => i !== idx);
    setFieldValue('instruction_steps', updatedData);
  };

  const addIngredients = () => {
    setFieldValue('ingredients', [
      ...value.ingredients,
      {
        value: ''
      }
    ]);
  };

  const removeIngredients = (idx) => {
    const updatedData = value.ingredients.filter((_, i) => i !== idx);
    setFieldValue('ingredients', updatedData);
  };

  const updateIngredients = (idx, field, val) => {
    const updated = [...value.ingredients];
    updated[idx] = { ...updated[idx], [field]: val };
    setFieldValue('ingredients', updated);
  };

  const updateInstruction = (idx, field, val) => {
    const updated = [...value.instruction_steps];
    updated[idx] = { ...updated[idx], [field]: val };
    setFieldValue('instruction_steps', updated);
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <p className="text-[14px] font-medium mb-2 text-[#605D66]">Food Item</p>

        {value.ingredients &&
          value.ingredients.map((item, idx) => (
            <div className="flex flex-col gap-1 mb-3" key={idx}>
              <input
                className="border-[1px] border-[#DEE0E3] text-sm font-normal placeholder:text-[#0D112666] rounded-md p-2 flex-1 outline-none duration-700 ease-in-out focus:border-[#003333] focus:border-opacity-50 focus:shadow-[#003333] focus:ring focus:ring-[#ACD1D1] focus:ring-opacity-30"
                type="text"
                name="value"
                placeholder="E.g Rice"
                required
                value={item.value}
                onChange={(e) => {
                  updateIngredients(idx, 'value', e.target.value);
                }}
              />

              <div className="flex justify-end">
                <div className="flex gap-1 items-center">
                  {value?.ingredients?.length > 1 && (
                    <div onClick={() => removeIngredients(idx)}>
                      {' '}
                      <MdDeleteOutline size={20} className="text-red-500" />{' '}
                    </div>
                  )}
                  <div onClick={addIngredients}>
                    <IoMdAddCircleOutline size={20} color="#549a9a" />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>

      <div>
        <p className="text-[14px] font-medium mb-2 text-[#605D66]">Instructions</p>

        {value.instruction_steps &&
          value.instruction_steps.map((item, idx) => (
            <div
              key={idx}
              className="flex flex-col gap-2 mb-4 p-3 border border-[#DEE0E3] rounded-md"
            >
              <input
                className="border-[1px] border-[#DEE0E3] text-sm font-normal placeholder:text-[#0D112666] rounded-md p-2 flex-1 outline-none duration-700 ease-in-out focus:border-[#003333] focus:border-opacity-50 focus:shadow-[#003333] focus:ring focus:ring-[#ACD1D1] focus:ring-opacity-30"
                type="text"
                name="step"
                placeholder="E.g Boil the rice"
                required
                value={item.step || ''}
                onChange={(e) => {
                  updateInstruction(idx, 'step', e.target.value);
                }}
              />

              <input
                className="border-[1px] border-[#DEE0E3] text-sm font-normal placeholder:text-[#0D112666] rounded-md p-2 flex-1 outline-none duration-700 ease-in-out focus:border-[#003333] focus:border-opacity-50 focus:shadow-[#003333] focus:ring focus:ring-[#ACD1D1] focus:ring-opacity-30"
                type="text"
                inputMode="numeric"
                name="time_estimate"
                placeholder="Time estimate (minutes)"
                value={item.time_estimate || ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  const numericOnly = raw.replace(/[^0-9]/g, '');
                  updateInstruction(
                    idx,
                    'time_estimate',
                    numericOnly === '' ? '' : Number(numericOnly)
                  );
                }}
              />

              <div className="flex flex-col gap-2">
                <label className="text-[12px] font-medium text-[#605D66]">
                  Step Image (optional)
                </label>
                <input
                  ref={(el) => (fileInputRefs.current[idx] = el)}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => handleImageSelect(e, idx)}
                />
                {item.image_url ? (
                  <div className="relative border border-[#DEE0E3] rounded-md p-2">
                    <img
                      src={item.image_url}
                      alt={`Step ${idx + 1}`}
                      className="object-cover w-full h-32 rounded-md"
                    />
                    <div
                      onClick={() => handleRemoveImage(idx)}
                      className="grid absolute top-3 right-3 place-items-center p-1 w-7 text-white bg-red-500 rounded-full cursor-pointer hover:bg-red-600"
                    >
                      <MdDeleteOutline size={16} />
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => handleImageClick(idx)}
                    className="border border-dashed border-[#DEE0E3] rounded-md p-4 text-center cursor-pointer hover:border-[#003333] hover:bg-[#00333305] transition-colors"
                  >
                    {uploadingImages[idx] ? (
                      <div className="flex flex-col gap-2 items-center">
                        <ImSpinner
                          className="animate-spin"
                          size={20}
                          color="#003333"
                        />
                        <p className="text-[12px] text-[#605D66]">Uploading...</p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1 items-center">
                        <p className="text-[12px] text-[#605D66]">
                          Click to upload image
                        </p>
                        <p className="text-[10px] text-[#0D112666]">
                          PNG, JPG or JPEG
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <button
                  disabled={generatingImages[idx]}
                  className="disbaled:opacity-50"
                  onClick={() => generateStepImage(idx)}
                  type="button"
                >
                  {generatingImages[idx] ? 'Generating' : 'Generate Image'}
                </button>
              </div>

              <input
                className="border-[1px] border-[#DEE0E3] text-sm font-normal placeholder:text-[#0D112666] rounded-md p-2 flex-1 outline-none duration-700 ease-in-out focus:border-[#003333] focus:border-opacity-50 focus:shadow-[#003333] focus:ring focus:ring-[#ACD1D1] focus:ring-opacity-30"
                type="url"
                name="video_url"
                placeholder="Video URL (optional)"
                value={item.video_url || ''}
                onChange={(e) => {
                  updateInstruction(idx, 'video_url', e.target.value || undefined);
                }}
              />

              <div className="flex justify-end">
                <div className="flex gap-1 items-center">
                  {value?.instruction_steps?.length > 1 && (
                    <div onClick={() => removeInstruction(idx)}>
                      {' '}
                      <MdDeleteOutline size={20} className="text-red-500" />{' '}
                    </div>
                  )}
                  <div onClick={addInstruction}>
                    <IoMdAddCircleOutline size={20} color="#549a9a" />
                  </div>
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default CookingSteps;
